<script setup lang="ts">
import PostalAddress from '@/components/base/PostalAddress.vue';
import LargeBodyText from '@/components/base/typography/LargeBodyText.vue';
import ShippingInfo from '@/components/checkout/orderReceipt/ShippingInfo.vue';
import { containsGiftCertificate, hasOnlyGiftCertificate, Shipment } from '@/utils/cart';

interface Props {
  orderNumber: string;
  shipments: Shipment[];
  showPricing?: boolean;
}
withDefaults(defineProps<Props>(), {
  orderNumber: 'none',
  showPricing: true,
});

const itemsBubbleStyles = [
  'border',
  'border-black',
  'border-solid',
  'cursor-pointer',
  'flex',
  'h-10',
  'items-center',
  'px-5',
  'py-2',
  'rounded-full',
  'w-max',
  'text-sm',
  'md:text-base',
];
</script>

<template>
  <div class="text-sm md:text-base">
    <div
      v-for="shipment in shipments"
      :key="shipment.key"
      :class="{ 'mt-10': shipments.length > 1 }"
    >
      <LargeBodyText class="font-sofia-pro">
        <template v-if="shipments.length > 1">Address {{ shipment.multishipSerial }}</template>
        <template
          v-else-if="!hasOnlyGiftCertificate(shipment.lineItems) && shipment.address.street1"
        >
          Shipping Address
        </template>
      </LargeBodyText>
      <template v-if="!hasOnlyGiftCertificate(shipment.lineItems)">
        <div v-if="shipment.address.street1" class="flex items-end justify-between w-full">
          <PostalAddress :address="shipment.address" class="mt-2 leading-5" />
          <div v-if="shipments.length > 1" :class="itemsBubbleStyles">
            <img
              :src="shipment.lineItems[0].variant.images?.[0].url"
              :alt="shipment.lineItems[0].name.en"
              class="h-8 mr-1"
            />
            {{ shipment.lineItems.length }} Item{{ shipment.lineItems.length > 1 ? 's' : '' }}
          </div>
        </div>
        <ShippingInfo
          v-if="shipments.length > 1"
          :shipment="shipment"
          :showPricing="showPricing"
          class="mt-4"
        />
        <div v-else class="mt-6">
          <LargeBodyText class="font-sofia-pro">Delivery Method</LargeBodyText>
          <ShippingInfo class="mt-2" :shipment="shipment" :showPricing="showPricing" />
        </div>
      </template>
      <div
        v-if="containsGiftCertificate(shipment.lineItems)"
        class="mb-2 text-sm leading-4 md:leading-5 md:text-base"
        :class="{ 'pt-4 md:pt-5': !hasOnlyGiftCertificate(shipment.lineItems) }"
      >
        <strong class="leading-5 text-small md:text-base">Gift Certificate Recipient</strong>
        <div v-if="hasOnlyGiftCertificate(shipment.lineItems)">{{ shipment.address.name }}</div>
        <div>{{ shipment.address.email }}</div>
      </div>
      <div v-if="hasOnlyGiftCertificate(shipment.lineItems)">
        Digital Delivery - Arrives Immediately
      </div>
      <div class="flex flex-col mt-8">
        <RouterLink
          :to="`/orders/${orderNumber}${
            shipment.address.postalCode ? `?postalCode=${shipment.address.postalCode}` : ''
          }`"
          class="pb-2 text-base underline view-order"
        >
          View order details
        </RouterLink>
        <RouterLink to="/account/order-history" class="pb-2 text-base underline view-order">
          See past orders
        </RouterLink>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.view-order {
  color: #333333;
}
</style>
